import { Button, formatMoney, TextButton } from "@majitap/ui";
import { Alert } from "@majitap/ui/arc/alert/alert";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Lock, PackageOpen, X } from "lucide-react";
import { useState } from "react";
import { api, errorOf, ok } from "../../api";
import { useCompanyFormat } from "../../company";
import {
  type BoardRow,
  bestFit,
  type Candidate,
  dispatchActions,
  fillsiteOptionsQuery,
  fleetQueries,
  litresOf,
} from "../../dispatch";
import { messageFor } from "../../error-copy";
import { DELIVERY_STATUS, type DeliveryStatus } from "../../labels";
import { ageOf, kmBetween, type TruckPosition, truckPositionsQuery } from "../../tracking";
import { useRefreshBoard } from "./actions";
import styles from "./board.module.css";
import { Avatar, PaneHead } from "./pane";

const litresText = (l: number) => `${l.toLocaleString("en-KE")} L`;
const ownerOf = (f: { partner: { name: string } | null }) => (f.partner ? `${f.partner.name}'s` : "own site");

/** On a delivery: from assigned until it is delivered or stops. */
const ON_A_JOB = new Set(["assigned", "accepted", "at_fillsite", "in_transit", "at_customer", "suspended"]);

/** Approved fill sites, nearest to the place first. */
export function useFillsitesNear(place: BoardRow["location"]) {
  const fillsites = useQuery(fleetQueries.fillsites);
  return [...(fillsites.data ?? [])]
    .map((f) => ({ ...f, km: kmBetween(f, place) }))
    .sort((a, b) => (a.km ?? Number.POSITIVE_INFINITY) - (b.km ?? Number.POSITIVE_INFINITY));
}

/**
 * Step 1 of planning a load (`Console / Dispatch / Board / Fill site`): where the truck fills up,
 * nearest to the place first. Drivers come once it has one.
 */
export function FillsitePanel({ row, onClose }: { row: BoardRow; onClose: () => void }) {
  const refresh = useRefreshBoard();
  const litres = litresOf(row);
  const format = useCompanyFormat();
  const options = useQuery(fillsiteOptionsQuery(row.id));
  const sites = options.data?.data ?? [];
  const [picked, setPicked] = useState<string | null>(row.assignment?.fillsite?.id ?? null);
  const chosen = sites.find((f) => f.id === picked) ?? sites[0];
  const use = useMutation({
    mutationFn: (id: string) => dispatchActions.fillsite(row.id, id),
    onSuccess: () => refresh(),
  });
  return (
    <section className={styles.side} aria-label={`Plan ${row.delivery_number}`}>
      <PaneHead
        title={`Plan ${row.delivery_number}`}
        sub={[row.order.customer_name, row.location?.label, litresText(litres)].filter(Boolean).join(" · ")}
        onClose={onClose}
      />
      <div className={styles.sideBody}>
        <div className={styles.step}>
          <h3 className={styles.stepHead}>
            <span className={styles.stepNumber}>1</span> Fill site
          </h3>
          <p className={styles.small}>
            {`Where the truck fills up. Nearest first; the water cost is this site's rate for ${litresText(litres)}.`}
          </p>
          {options.status === "pending" ? (
            <p className={styles.small}>Finding fill sites…</p>
          ) : sites.length === 0 ? (
            <p className={styles.small}>No approved fill site is in use. Add one under Fleet › Fill sites.</p>
          ) : (
            <div className={styles.options} role="radiogroup" aria-label="Fill sites, nearest first">
              {sites.slice(0, 5).map((f) => (
                // biome-ignore lint/a11y/useSemanticElements: a card-sized radio, styled as the design
                <button
                  key={f.id}
                  type="button"
                  role="radio"
                  aria-checked={f.id === chosen?.id}
                  className={styles.radioOption}
                  onClick={() => setPicked(f.id)}
                >
                  <span className={styles.radioDot} aria-hidden />
                  <span className={styles.optionBody}>
                    <span className={styles.optionTop}>
                      <strong>{f.name}</strong>
                      {f.nearest ? (
                        <span className={styles.chip} data-tone="success">
                          Nearest
                        </span>
                      ) : null}
                      {f.cheapest ? (
                        <span className={styles.chip} data-tone="info">
                          Cheapest
                        </span>
                      ) : null}
                    </span>
                    <span className={styles.small}>
                      {[f.distance_km === null ? null : `${f.distance_km} km`, ownerOf(f)].filter(Boolean).join(" · ")}
                    </span>
                  </span>
                  {f.water_cost !== null ? (
                    <strong className={styles.price}>
                      {formatMoney(f.water_cost, { currency: options.data?.currency ?? format.currency })}
                    </strong>
                  ) : null}
                </button>
              ))}
            </div>
          )}
          {use.error ? (
            <Alert tone="danger" title="Not done">
              {messageFor(errorOf(use.error))}
            </Alert>
          ) : null}
          {chosen ? (
            <Button className={styles.wide} loading={use.isPending} onClick={() => use.mutate(chosen.id)}>
              {`Use ${chosen.name}`}
            </Button>
          ) : null}
        </div>
        <div className={styles.step} data-locked="true">
          <h3 className={styles.stepHead}>
            <span className={styles.stepNumber}>2</span> Driver on shift
          </h3>
          <p className={styles.lockedNote}>
            <Lock size={14} aria-hidden />
            {`Pick a fill site first. Free drivers with a truck of ${litresText(litres)} or more show here.`}
          </p>
        </div>
      </div>
      <footer className={styles.sideNote}>A parcel has no fill site and skips step 1.</footer>
    </section>
  );
}

/**
 * Drivers on shift (`Console / Dispatch / Board`, right): for the selected delivery, the best fit
 * first (free, a truck big enough, nearest the fill site), then free drivers whose truck is too
 * small, then those on a job. With nothing selected, who is free and who is busy.
 */
export function DriversPanel({
  rows,
  row,
  onChangeFillsite,
  onClose,
  onPostpay,
}: {
  rows: BoardRow[];
  row: BoardRow | null;
  onChangeFillsite: () => void;
  onClose: () => void;
  onPostpay?: (row: BoardRow) => void;
}) {
  const refresh = useRefreshBoard();
  const shifts = useQuery(fleetQueries.shifts);
  const fillsites = useQuery(fleetQueries.fillsites);
  const positions = useQuery(truckPositionsQuery);
  const trucks = useQuery({
    queryKey: ["fleet", "trucks", "active"],
    staleTime: 60_000,
    queryFn: () => ok(api.GET("/v1/admin/fleet/trucks", { params: { query: { status: "active", limit: 100 } } })),
  });
  const [only, setOnly] = useState<"all" | "free">("all");
  const litres = row ? litresOf(row) : 0;
  const site = fillsites.data?.find((f) => f.id === row?.assignment?.fillsite?.id) ?? null;
  // where the truck goes first: the fill site for a load, the place for a parcel
  const target = row ? (site ?? row.location) : null;
  const targetName = row ? (site?.name ?? "the place") : null;

  const assign = useMutation({
    mutationFn: (driverId: string) => dispatchActions.assign(row?.id ?? "", { driver_id: driverId }),
    onSuccess: () => refresh(),
  });

  const positionOf = (truckId: string) => positions.data?.data.find((p) => p.truck_id === truckId);
  /** the delivery a driver is on: from the truck's phone, else from the board */
  const jobOf = (driverId: string, truckId: string | undefined) => {
    const p = truckId ? positionOf(truckId)?.delivery : null;
    const onBoard = rows.find((r) => r.assignment?.driver?.id === driverId && ON_A_JOB.has(r.status));
    return p ? (rows.find((r) => r.id === p.id) ?? { ...p, location: null }) : (onBoard ?? null);
  };
  const kmOf = (truckId: string) => {
    const p = positionOf(truckId);
    return p && !p.stale && target ? kmBetween(p, target) : null;
  };
  const candidates = bestFit(shifts.data ?? [], litres, kmOf, (s) => jobOf(s.driver.id, s.truck?.id) !== null);
  const free = candidates.filter((c) => c.free);
  const groups: [string, Candidate[], boolean][] = row
    ? [
        ["Best fit · free, big enough, nearest fill site", free.filter((c) => c.fits), true],
        ["Free, truck too small", free.filter((c) => !c.fits), false],
        [
          `On a job · ${candidates.length - free.length}`,
          only === "all" ? candidates.filter((c) => !c.free) : [],
          false,
        ],
      ]
    : [
        [`Free · ${free.length}`, free, false],
        [
          `On a job · ${candidates.length - free.length}`,
          only === "all" ? candidates.filter((c) => !c.free) : [],
          false,
        ],
      ];
  const onShift = new Set((shifts.data ?? []).map((s) => s.truck?.id));
  const idleTrucks = (trucks.data?.data ?? []).filter((t) => !onShift.has(t.id)).length;
  const failure = assign.error ? errorOf(assign.error) : null;

  return (
    <section className={styles.side} aria-label="Drivers on shift">
      <PaneHead
        title="Drivers on shift"
        aside={
          <span className={styles.toggle}>
            {(
              [
                ["all", `All ${candidates.length}`],
                ["free", `Free ${free.length}`],
              ] as const
            ).map(([v, label]) => (
              <button key={v} type="button" aria-pressed={only === v} onClick={() => setOnly(v)}>
                {label}
              </button>
            ))}
          </span>
        }
      />
      {row ? (
        <div className={styles.context}>
          <PackageOpen size={16} aria-hidden />
          <span>
            {[row.delivery_number, site ? `${site.name}` : litres ? null : "a parcel"].filter(Boolean).join(" · ")}
          </span>
          {litres ? <TextButton onClick={onChangeFillsite}>Change</TextButton> : null}
          <button type="button" className={styles.iconButton} aria-label="Close" onClick={onClose}>
            <X size={14} aria-hidden />
          </button>
        </div>
      ) : null}
      <div className={styles.sideBody}>
        {failure ? (
          <Alert tone="danger" title="Not assigned">
            {messageFor(failure)}
            {row && onPostpay && ["PAYMENT_REQUIRED", "CREDIT_LIMIT_EXCEEDED"].includes(failure.code) ? (
              <div>
                <TextButton onClick={() => onPostpay(row)}>
                  {failure.code === "CREDIT_LIMIT_EXCEEDED"
                    ? "Deliver anyway, over the credit limit"
                    : "Deliver before payment"}
                </TextButton>
              </div>
            ) : null}
          </Alert>
        ) : null}
        {candidates.length === 0 ? (
          <p className={styles.small}>Nobody is on shift. Drivers start a shift in the driver app.</p>
        ) : (
          groups
            .filter(([, list]) => list.length > 0)
            .map(([title, list, canAssign]) => (
              <section key={title} className={styles.paneSection} aria-label={title}>
                <h3 className={styles.sectionLabel}>{title}</h3>
                <ul className={styles.drivers}>
                  {list.map((c) => (
                    <DriverRow
                      key={c.shift.id}
                      c={c}
                      litres={litres}
                      position={c.shift.truck ? positionOf(c.shift.truck.id) : undefined}
                      job={jobOf(c.shift.driver.id, c.shift.truck?.id)}
                      targetName={targetName}
                      assign={
                        row && canAssign
                          ? {
                              run: () => assign.mutate(c.shift.driver.id),
                              pending: assign.isPending && assign.variables === c.shift.driver.id,
                            }
                          : null
                      }
                    />
                  ))}
                </ul>
              </section>
            ))
        )}
      </div>
      <footer className={styles.sideNote}>
        <span>
          {idleTrucks === 1 ? "1 truck without a driver today" : `${idleTrucks} trucks without a driver today`}
        </span>
        <Link to="/fleet" className={styles.link}>
          Fleet
        </Link>
      </footer>
    </section>
  );
}

function DriverRow({
  c,
  litres,
  position: p,
  job,
  targetName,
  assign,
}: {
  c: Candidate;
  litres: number;
  position: TruckPosition | undefined;
  job: { delivery_number: string; status: string; location: BoardRow["location"] } | null;
  targetName: string | null;
  assign: { run: () => void; pending: boolean } | null;
}) {
  const truck = c.shift.truck;
  const stale = !p || p.stale;
  const seen = p ? `${ageOf(p.recorded_at)} ago` : null;
  let line: string;
  let tone: "success" | "warning" | "accent";
  if (!c.free && job) {
    tone = "accent";
    line = `${DELIVERY_STATUS[job.status as DeliveryStatus]?.[0] ?? job.status} · ${job.delivery_number}`;
  } else if (stale) {
    tone = "warning";
    line = p ? `Last seen ${seen} · phone offline?` : "No location yet";
  } else {
    tone = "success";
    line = ["Free", c.km !== null && targetName ? `${c.km} km from ${targetName}` : null, seen]
      .filter(Boolean)
      .join(" · ");
  }
  const kmToGo = !c.free && job?.location && p && !p.stale ? kmBetween(p, job.location) : null;
  return (
    <li className={styles.driver} data-best={assign ? "true" : undefined}>
      <Avatar name={c.shift.driver.name} />
      <span className={styles.optionBody}>
        <strong>{c.shift.driver.name}</strong>
        <span className={styles.small}>
          {[truck?.plate_number, truck ? litresText(truck.capacity_litres) : null].filter(Boolean).join(" · ")}
        </span>
        <span className={styles.status} data-tone={tone}>
          {line}
        </span>
      </span>
      {assign ? (
        <Button size="sm" loading={assign.pending} onClick={assign.run}>
          Assign
        </Button>
      ) : !c.fits && litres > 0 && truck ? (
        <span className={styles.small}>{`Max ${litresText(truck.capacity_litres)}`}</span>
      ) : kmToGo !== null ? (
        <span className={styles.small}>{`${kmToGo} km to go`}</span>
      ) : null}
    </li>
  );
}
